/**
 * useKeyboardShortcuts — bind a map of key → handler to window keydown.
 *
 * The Practice page is the main consumer (Space to advance, R to replay,
 * 1-9 to pick an option). The hook stays generic so other pages can opt
 * in later.
 *
 * Two important behaviors:
 *
 *   1. We skip events fired while focus is in an editable element
 *      (input, textarea, select, contenteditable). Otherwise typing in a
 *      future settings field would trigger practice shortcuts.
 *
 *   2. We accept a stable map across renders by reading from a ref so the
 *      effect never re-binds on every state change. The listener pulls the
 *      latest map at fire time.
 */

import { useEffect, useRef } from "react";

export type ShortcutHandler = (event: KeyboardEvent) => void;
export type ShortcutMap = Record<string, ShortcutHandler | undefined>;

export interface UseKeyboardShortcutsOptions {
  /** When false, all shortcuts are inert (e.g. modal open). Default true. */
  enabled?: boolean;
}

export function useKeyboardShortcuts(
  shortcuts: ShortcutMap,
  options: UseKeyboardShortcutsOptions = {},
): void {
  const { enabled = true } = options;
  const mapRef = useRef(shortcuts);
  mapRef.current = shortcuts;

  useEffect(() => {
    if (!enabled) return;

    const onKey = (event: KeyboardEvent): void => {
      if (isEditableTarget(event.target)) return;
      // Ignore when modifier keys are held — we don't want to steal Cmd+R.
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const key = normalizeKey(event);
      const handler = mapRef.current[key];
      if (!handler) return;
      handler(event);
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [enabled]);
}

/**
 * Normalize a KeyboardEvent to a stable lookup string:
 *   - Letters lowercased ("R" → "r")
 *   - Digits as their character ("Digit3" → "3", "Numpad3" → "3")
 *   - Space → "space"
 *   - Enter → "enter"
 *   - Escape → "escape"
 *
 * Exported for testability — the hook itself is mostly listener wiring.
 */
export function normalizeKey(event: Pick<KeyboardEvent, "code" | "key">): string {
  if (event.code === "Space") return "space";
  if (event.code === "Enter") return "enter";
  if (event.code === "Escape") return "escape";
  // Digit / Numpad keys.
  if (/^Digit\d$/.test(event.code)) return event.code.slice(-1);
  if (/^Numpad\d$/.test(event.code)) return event.code.slice(-1);
  // Letters and everything else.
  return event.key.toLowerCase();
}

export function isEditableTarget(target: EventTarget | null): boolean {
  if (target === null) return false;
  // Duck-type instead of `instanceof HTMLElement` so this is testable in
  // a node environment without jsdom: HTML element targets always carry
  // tagName and isContentEditable.
  const t = target as Partial<HTMLElement>;
  if (t.isContentEditable === true) return true;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}
